import React, { useState, useEffect, useRef } from 'react';
import { Outlet, Link, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useTheme } from 'next-themes';
import { Button } from '@/components/ui/button';
import { UserProvider } from '@/lib/UserContext';
import { FilterProvider } from '@/lib/FilterContext';
import { ReviewBadge } from '@/components/ReviewBadge';
import { AbuseBadge } from '@/components/AbuseBadge';
import {
  LayoutDashboard,
  Upload,
  TrendingUp,
  Users,
  Shield,
  FileText,
  Settings,
  LogOut,
  Menu,
  X,
  Building2,
  UserCog,
  ArrowRightLeft,
  Activity,
  ClipboardList,
  Lock,
  Sun,
  Moon,
  Cable,
  Clock,
  ChevronDown,
  HelpCircle,
  ChevronsLeft,
  ChevronsRight,
  ShieldCheck,
  FileSearch,
  AlertTriangle,
  UsersRound,
} from 'lucide-react';

export default function DashboardLayout({ user, onLogout }) {
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [expandedNav, setExpandedNav] = useState(null);
  const [collapsed, setCollapsed] = useState(() => {
    try { return localStorage.getItem('sidebar_collapsed') === 'true'; } catch { return false; }
  });
  const { theme, setTheme } = useTheme();
  const mainRef = useRef(null);

  // Reset scroll position to the top on every route change.
  // The main content container is the scrolling element, not window.
  useEffect(() => {
    if (mainRef.current) {
      mainRef.current.scrollTop = 0;
    }
    // Also reset window scroll for good measure (mobile Safari edge cases)
    try { window.scrollTo(0, 0); } catch { /* noop */ }
  }, [location.pathname]);

  const toggleCollapsed = () => {
    const next = !collapsed;
    setCollapsed(next);
    try { localStorage.setItem('sidebar_collapsed', String(next)); } catch {}
  };

  const isSuperAdmin = (user?.roles || []).includes('super_admin');
  const userPages = new Set(user?.page_permissions || []);

  const allNavigation = [
    { name: 'Dashboard', href: '/', icon: LayoutDashboard },
    { name: 'Upload Data', href: '/upload', icon: Upload },
    { name: 'Spend Analysis', href: '/spend-analysis', icon: TrendingUp },
    { name: 'Spend Distribution', href: '/spend-distribution', icon: TrendingUp },
    { name: 'Category Console', href: '/category-console', icon: TrendingUp },
    { name: 'Commonality', href: '/commonality-analysis', icon: Users },
    { name: 'Supplier Directory', href: '/supplier-directory', icon: Building2,
      children: [{ name: 'Supplier Management', href: '/suppliers', icon: Users }] },
    { name: 'P2P Analytics', href: '/p2p-analytics', icon: ArrowRightLeft },
    { name: 'On-Contract / Maverick', href: '/on-contract-maverick', icon: ShieldCheck },
    { name: 'Invoice Dedup', href: '/invoice-dedup', icon: FileSearch },
    { name: 'Comparative', href: '/comparative-analysis', icon: TrendingUp },
    { name: 'Benchmark', href: '/industry-benchmark', icon: Building2 },
    { name: 'Forecasting', href: '/forecasting', icon: TrendingUp },
    { name: 'Currency', href: '/currency-analytics', icon: ArrowRightLeft },
    { name: 'Reports', href: '/reports', icon: FileText },
    { name: 'Data Quality', href: '/data-quality', icon: Activity },
    { name: 'Governance', href: '/governance', icon: Shield },
    { name: 'Invoice Aging', href: '/invoice-aging', icon: Clock },
    { name: 'Connectors', href: '/connectors', icon: Cable },
    { name: 'User Management', href: '/user-management', icon: UserCog },
    { name: 'Platform Admin', href: '/admin/platform', icon: UsersRound },
    { name: 'Audit Log', href: '/audit-log', icon: ClipboardList },
    { name: 'Abuse Events', href: '/admin/abuse-events', icon: AlertTriangle },
    { name: 'Privacy Policy', href: '/privacy-policy', icon: Lock },
    { name: 'Settings', href: '/settings', icon: Settings },
    { name: 'Help', href: '/help', icon: HelpCircle },
  ];

  const navigation = isSuperAdmin
    ? allNavigation
    : allNavigation.filter(item => userPages.has(item.href)).map(item => ({
        ...item,
        children: item.children?.filter(child => userPages.has(child.href)),
      }));

  return (
    <div className="flex h-screen bg-background" data-testid="dashboard-layout">
      {/* Mobile sidebar backdrop */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 z-40 bg-black bg-opacity-50 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside
        className={`fixed inset-y-0 left-0 z-50 bg-card border-r border-border transition-all duration-300 lg:static lg:translate-x-0 ${
          sidebarOpen ? 'translate-x-0' : '-translate-x-full'
        } ${collapsed ? 'lg:w-14' : 'lg:w-52'} ${collapsed ? 'w-14' : 'w-52'}`}
      >
        <div className="flex flex-col h-full">
          {/* Logo + collapse toggle */}
          <div className="px-4 py-5 border-b border-border">
            <div className="flex items-start justify-between gap-2">
              {!collapsed ? (
                <div className="flex items-center flex-1 min-w-0">
                  <img
                    src="/brand/logo.png"
                    alt="ProcureAide"
                    className="h-14 w-auto max-w-full object-contain"
                  />
                </div>
              ) : (
                <div className="flex items-center justify-center w-full">
                  <img
                    src="/brand/logo.png"
                    alt="P"
                    className="h-10 object-contain"
                  />
                </div>
              )}
              {/* Collapse toggle - desktop only */}
              <button
                className="hidden lg:flex items-center justify-center w-6 h-6 rounded-md hover:bg-accent transition-colors shrink-0"
                onClick={toggleCollapsed}
                data-testid="sidebar-collapse-toggle"
                title={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
              >
                {collapsed ? <ChevronsRight className="w-4 h-4 text-muted-foreground" /> : <ChevronsLeft className="w-4 h-4 text-muted-foreground" />}
              </button>
              {/* Mobile close */}
              <button
                className="lg:hidden"
                onClick={() => setSidebarOpen(false)}
                data-testid="close-sidebar-button"
              >
                <X className="w-6 h-6" />
              </button>
            </div>
          </div>

          {/* Navigation */}
          <nav className={`flex-1 overflow-y-auto ${collapsed ? 'p-1' : 'p-2'} space-y-0.5`}>
            {navigation.map((item) => {
              const isActive = location.pathname === item.href;
              const isChildActive = item.children?.some(c => location.pathname === c.href);
              const hasChildren = item.children?.length > 0;
              return (
                <React.Fragment key={item.name}>
                  <div className="relative group">
                    <div className="flex items-center">
                      <Link
                        to={item.href}
                        data-testid={`nav-link-${item.name.toLowerCase().replace(/\s+/g, '-')}`}
                        className={`flex-1 flex items-center rounded-md transition-all duration-200 ${
                          collapsed ? 'justify-center px-0 py-2' : 'gap-2.5 px-3 py-1.5'
                        } text-[13px] ${
                          isActive || isChildActive
                            ? 'bg-primary text-primary-foreground shadow-md'
                            : 'text-muted-foreground hover:bg-accent hover:text-accent-foreground'
                        }`}
                        onClick={() => setSidebarOpen(false)}
                      >
                        <item.icon className="w-4 h-4 shrink-0" />
                        {!collapsed && <span className="font-medium">{item.name}</span>}
                      </Link>
                      {hasChildren && !collapsed && (
                        <button
                          onClick={() => setExpandedNav(prev => prev === item.name ? null : item.name)}
                          data-testid={`toggle-${item.name.toLowerCase().replace(/\s+/g, '-')}`}
                          className={`p-1 rounded-md transition-colors ${
                            isActive || isChildActive
                              ? 'text-primary-foreground/70 hover:text-primary-foreground'
                              : 'text-muted-foreground/50 hover:text-muted-foreground'
                          }`}
                        >
                          <ChevronDown className={`w-3.5 h-3.5 transition-transform duration-200 ${expandedNav === item.name || isChildActive ? 'rotate-180' : ''}`} />
                        </button>
                      )}
                    </div>
                    {/* Tooltip when collapsed */}
                    {collapsed && (
                      <div className="absolute left-full top-1/2 -translate-y-1/2 ml-2 px-2.5 py-1 rounded-md bg-popover border border-border shadow-md text-xs font-medium whitespace-nowrap opacity-0 group-hover:opacity-100 pointer-events-none transition-opacity z-50" data-testid={`tooltip-${item.name.toLowerCase().replace(/\s+/g, '-')}`}>
                        {item.name}
                      </div>
                    )}
                  </div>
                  {hasChildren && !collapsed && (expandedNav === item.name || isChildActive) && (
                    <div className="overflow-hidden">
                      {item.children.map((child) => {
                        const childActive = location.pathname === child.href;
                        return (
                          <Link
                            key={child.name}
                            to={child.href}
                            data-testid={`nav-link-${child.name.toLowerCase().replace(/\s+/g, '-')}`}
                            className={`flex items-center gap-2.5 pl-9 pr-3 py-1 rounded-md text-[12px] transition-all duration-200 ${
                              childActive
                                ? 'bg-primary/90 text-primary-foreground shadow-sm'
                                : 'text-muted-foreground/80 hover:bg-accent hover:text-accent-foreground'
                            }`}
                            onClick={() => setSidebarOpen(false)}
                          >
                            <child.icon className="w-3.5 h-3.5 shrink-0" />
                            <span className="font-medium">{child.name}</span>
                          </Link>
                        );
                      })}
                    </div>
                  )}
                </React.Fragment>
              );
            })}
          </nav>

          {/* User profile — DataRefinery-style stacked card */}
          <div className={`border-t border-border ${collapsed ? 'p-1.5' : 'p-3'}`}>
            {collapsed ? (
              <div className="relative group">
                <div className="flex items-center justify-center">
                  <div className="w-7 h-7 rounded-full bg-primary flex items-center justify-center text-primary-foreground text-xs font-semibold cursor-default">
                    {user?.full_name?.charAt(0).toUpperCase()}
                  </div>
                </div>
                <div className="absolute left-full bottom-0 ml-2 px-3 py-2 rounded-md bg-popover border border-border shadow-md text-xs whitespace-nowrap opacity-0 group-hover:opacity-100 pointer-events-none transition-opacity z-50">
                  {user?.organization_name && <p className="font-semibold">{user.organization_name}</p>}
                  <p className="font-medium">{user?.full_name}</p>
                  <p className="text-muted-foreground">{user?.email}</p>
                </div>
              </div>
            ) : (
              <>
                <div className="mb-2 space-y-0.5" data-testid="sidebar-user-card">
                  {user?.organization_name && (
                    <p className="text-sm font-semibold text-foreground truncate" data-testid="sidebar-org-name">
                      {user.organization_name}
                    </p>
                  )}
                  <p className="text-sm font-semibold text-foreground truncate" data-testid="sidebar-user-name">
                    {user?.full_name}
                  </p>
                  <div className="flex items-center gap-1.5 flex-wrap">
                    {(() => {
                      const roles = user?.roles || [];
                      const primary =
                        roles.includes('super_admin') ? { label: 'Super User', cls: 'bg-primary/15 text-primary' } :
                        roles.includes('admin')       ? { label: 'Admin',      cls: 'bg-blue-500/15 text-blue-600' } :
                        roles.length                  ? { label: roles[0].replace('_', ' '), cls: 'bg-muted text-muted-foreground' } :
                                                        null;
                      return primary ? (
                        <span className={`text-[10px] font-medium px-2 py-0.5 rounded-full ${primary.cls} capitalize`} data-testid="sidebar-role-pill">
                          {primary.label}
                        </span>
                      ) : null;
                    })()}
                    <span className="text-[11px] text-muted-foreground truncate flex-1 min-w-0" data-testid="sidebar-user-email">
                      {user?.email}
                    </span>
                  </div>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  className="w-full"
                  onClick={onLogout}
                  data-testid="logout-button"
                >
                  <LogOut className="w-4 h-4 mr-2" />
                  Logout
                </Button>
              </>
            )}
            {collapsed && (
              <button
                onClick={onLogout}
                className="flex items-center justify-center w-full mt-1.5 p-1.5 rounded-md text-muted-foreground hover:bg-accent hover:text-accent-foreground transition-colors"
                data-testid="logout-button"
                title="Logout"
              >
                <LogOut className="w-4 h-4" />
              </button>
            )}
          </div>
        </div>
      </aside>

      {/* Main content */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Header */}
        <header className="bg-card border-b border-border px-4 py-2">
          <div className="flex items-center justify-between">
            <button
              className="lg:hidden"
              onClick={() => setSidebarOpen(true)}
              data-testid="open-sidebar-button"
            >
              <Menu className="w-5 h-5" />
            </button>
            <div className="flex-1 lg:flex-none">
              <h2 className="text-lg font-semibold tracking-tight">
                {navigation.find((n) => n.href === location.pathname)?.name
                  || navigation.flatMap(n => n.children || []).find(c => c.href === location.pathname)?.name
                  || 'Dashboard'}
              </h2>
            </div>
            <div className="flex items-center gap-2">
              <AbuseBadge />
              <ReviewBadge />
              <Button
                variant="ghost"
                size="icon"
                className="h-8 w-8"
                onClick={() => setTheme(theme === 'dark' ? 'light' : 'dark')}
                data-testid="theme-toggle-button"
              >
                {theme === 'dark' ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
              </Button>
            </div>
          </div>
        </header>

        {/* Page content */}
        <main ref={mainRef} className="flex-1 overflow-y-auto p-3 lg:p-5" data-testid="main-scroll-container">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3 }}>
            <UserProvider user={user}>
              <FilterProvider>
                <Outlet />
              </FilterProvider>
            </UserProvider>
          </motion.div>
        </main>
      </div>
    </div>
  );
}
